<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePlanLimits } from 'dashboard/composables/usePlanLimits';
import { useMapGetter } from 'dashboard/composables/store';

const router = useRouter();
const { accountId } = useAccount();
const { 
  fetchPlanStatus, 
  fetchExceedingResources,
  planExpiredModal,
  hidePlanExpiredModal 
} = usePlanLimits();

const currentUser = useMapGetter('getCurrentUser');
const processing = ref(false);
const exceedingData = ref(null);

// Check if current user is admin
const isAdmin = computed(() => {
  const accounts = currentUser.value?.accounts || [];
  const currentAccount = accounts.find(acc => acc.id === accountId.value);
  return currentAccount?.role === 'administrator';
});

// Check plan status on mount and when account changes
const checkPlanStatus = async () => {
  if (!accountId.value || !isAdmin.value) return;
  
  try {
    const status = await fetchPlanStatus();
    
    // Only show modal if plan is expired AND there are exceeding resources
    if (status?.is_expired && status?.has_exceeding) {
      const exceeding = await fetchExceedingResources();
      exceedingData.value = exceeding;
      
      planExpiredModal.value = {
        show: true,
        data: {
          ...status,
          exceeding,
        },
      };
    }
  } catch (error) {
    console.error('Error checking plan status:', error);
  }
};

// Watch for account changes to check status
watch(() => accountId.value, () => {
  if (accountId.value) {
    // Small delay to ensure user data is loaded
    setTimeout(checkPlanStatus, 500);
  }
}, { immediate: true });

// Navigate to billing page
const goToBilling = () => {
  hidePlanExpiredModal();
  router.push({ 
    name: 'omni_billing',
    params: { accountId: accountId.value }
  });
};

// Close modal
const closeModal = () => {
  hidePlanExpiredModal();
};
</script>

<template>
  <Teleport to="body">
    <div 
      v-if="planExpiredModal.show" 
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
    >
      <div 
        class="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
        @click.stop
      >
        <!-- Header -->
        <div class="p-6 border-b border-slate-200 dark:border-slate-700 bg-red-600">
          <div class="flex items-center gap-3">
            <div class="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full">
              <span class="i-lucide-alert-triangle w-6 h-6 text-white" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white">
                Plano Expirado
              </h3>
              <p class="text-sm text-white/80">
                Sua assinatura precisa de atenção
              </p>
            </div>
          </div>
        </div>
        
        <!-- Content -->
        <div class="p-6 space-y-4">
          <div class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
            <div class="flex items-start gap-3">
              <span class="i-lucide-clock w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Sua assinatura expirou
                </p>
                <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Para continuar usando todos os recursos, renove sua assinatura ou faça um upgrade do seu plano.
                </p>
              </div>
            </div>
          </div>

          <!-- Exceeding Resources Summary -->
          <div v-if="exceedingData?.has_exceeding" class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
            <p class="text-sm font-medium text-red-800 dark:text-red-200 mb-3">
              Recursos que excedem o limite do plano:
            </p>
            <ul class="space-y-2">
              <li v-if="exceedingData.bots?.exceeding_count > 0" class="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <span class="i-lucide-bot w-4 h-4" />
                {{ exceedingData.bots.exceeding_count }} Atendente(s) IA
              </li>
              <li v-if="exceedingData.inboxes?.exceeding_count > 0" class="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <span class="i-lucide-inbox w-4 h-4" />
                {{ exceedingData.inboxes.exceeding_count }} Caixa(s) de Entrada
              </li>
              <li v-if="exceedingData.agents?.exceeding_count > 0" class="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <span class="i-lucide-users w-4 h-4" />
                {{ exceedingData.agents.exceeding_count }} Usuário(s)
              </li>
            </ul>
            <p class="text-xs text-red-600 dark:text-red-400 mt-3">
              Se não renovar, esses recursos serão desabilitados automaticamente.
            </p>
          </div>

          <div class="text-center">
            <p class="text-sm text-slate-500 dark:text-slate-400">
              Renove agora para manter todos os seus recursos ativos
            </p>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
          <div class="flex gap-3">
            <button 
              class="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              @click="closeModal"
            >
              Fazer depois
            </button>
            <button 
              class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-woot-600 to-woot-500 hover:from-woot-700 hover:to-woot-600 rounded-lg transition-all shadow-lg shadow-woot-500/25"
              @click="goToBilling"
            >
              <span class="i-lucide-credit-card w-4 h-4 inline-block mr-2" />
              Renovar Assinatura
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
